import { captureException } from '@sentry/react-router';

import { type GenericConfig } from '~/types';
import { type ComponentStateType } from '~/types/template';
import { invalidDehydratedState } from '~/utils/invalidDehydratedState.ts';

import { ComponentErrorBoundary } from '../components/ComponentErrorBoundary/ComponentErrorBoundary';
import { componentMapClient } from '../page-template/component-map';

import { isGroupComponent } from './isGroupComponent';

export function renderComponents({
  components,
  genericConfig,
}: {
  components: ComponentStateType[] | null;
  genericConfig: GenericConfig;
}) {
  if (!components) return null;

  return components.map((component, index) => {
    const id = component?.id ?? `failed-component-${index}`;
    // Hoisted so the catch can tag Sentry with the component name (matching
    // ComponentErrorBoundary.onError), falling back to the id if unavailable.
    const name = component?.name;

    try {
      const componentState = component.componentState;
      if (!name) return null;
      const componentObj = componentMapClient[name];
      if (!componentObj) return null;
      const Component = componentObj.Component;

      const componentProps = isGroupComponent(name) ? component : component.componentArgs;

      if (invalidDehydratedState(componentState)) return null;
      return (
        <ComponentErrorBoundary componentName={name} key={id}>
          <Component {...genericConfig} {...componentProps} dehydratedState={componentState} />
        </ComponentErrorBoundary>
      );
    } catch (error) {
      captureException(error, { tags: { component: name ?? id } });
      return null;
    }
  });
}
